import React, { useState, useRef } from 'react';
import { Upload, X, Camera, Image as ImageIcon, AlertCircle, CheckCircle } from 'lucide-react';
import Button from './Button';

const ImageUpload = ({ 
  label = "Foto", 
  value = null, 
  onChange, 
  error = null,
  accept = "image/*",
  maxSize = 5 * 1024 * 1024, // 5MB default
  className = "",
  placeholder = "Clique para adicionar uma foto",
  required = false,
  disabled = false
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(value);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validar se onChange é uma função
    if (typeof onChange !== 'function') {
      console.error('ImageUpload: onChange is not a function');
      return;
    }

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      onChange(null, 'Apenas arquivos de imagem são permitidos');
      return;
    }

    // Validar tamanho
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      onChange(null, `Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`);
      return;
    }

    setUploading(true);

    try {
      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Chamar callback com o arquivo
      onChange(file, null);
    } catch (error) {
      console.error('Error processing image:', error);
      onChange(null, 'Erro ao processar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };
  const handleRemove = () => {
    setPreview(null);
    if (typeof onChange === 'function') {
      onChange(null, null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
          ${dragOver 
            ? 'border-blue-400 bg-blue-50' 
            : error 
              ? 'border-red-300 bg-red-50' 
              : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
        />

        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="mx-auto max-h-48 max-w-full rounded-lg shadow-sm"
            />
            
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                title="Remover imagem"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            <div className="mt-3 flex items-center justify-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              Imagem carregada com sucesso
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-600 mt-2">Processando imagem...</p>
              </div>
            ) : (
              <>
                <div className="flex justify-center">
                  <div className={`p-3 rounded-full ${dragOver ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    {dragOver ? (
                      <Upload className="w-8 h-8 text-blue-600" />
                    ) : (
                      <Camera className="w-8 h-8 text-gray-600" />
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {placeholder}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ou arraste e solte uma imagem aqui
                  </p>
                </div>

                <div className="text-xs text-gray-400">
                  <p>Formatos: JPG, PNG, GIF, WEBP</p>
                  <p>Tamanho máximo: {formatFileSize(maxSize)}</p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center text-sm text-red-600">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </div>
      )}

      {/* Help Text */}
      {!error && !preview && (
        <p className="text-xs text-gray-500">
          Recomendamos imagens quadradas com boa resolução para melhor visualização
        </p>
      )}
    </div>
  );
};

export default ImageUpload;